import { Node, Beam } from '../types/nnstructural2D';

// Types for beam center operations
export interface Point2D {
  x: number;
  y: number;
}

export interface BeamCenterInfo {
  centerPoint: Point2D;
  length: number;
  angle: number;
}

export interface BeamSplitResult {
  newNode: Node;
  beam1: Beam;
  beam2: Beam;
  originalBeamId: string;
}

/**
 * Calculate the center point of a beam based on its start and end nodes
 */
export const calculateBeamCenter = (beam: Beam, nodes: Node[]): Point2D | null => {
  const startNode = nodes.find(n => n.id === beam.startNodeId);
  const endNode = nodes.find(n => n.id === beam.endNodeId);
  
  if (!startNode || !endNode) {
    return null;
  }

  return {
    x: (startNode.x + endNode.x) / 2,
    y: (startNode.y + endNode.y) / 2
  };
};

/**
 * Calculate the length of a beam
 */
export const calculateBeamLength = (beam: Beam, nodes: Node[]): number => {
  const startNode = nodes.find(n => n.id === beam.startNodeId);
  const endNode = nodes.find(n => n.id === beam.endNodeId);
  
  if (!startNode || !endNode) {
    return 0;
  }

  const dx = endNode.x - startNode.x;
  const dy = endNode.y - startNode.y;
  const dz = endNode.z - startNode.z;

  return Math.sqrt(dx * dx + dy * dy + dz * dz);
};

/**
 * Get comprehensive beam center information
 */
export const getBeamCenterInfo = (beam: Beam, nodes: Node[]): BeamCenterInfo | null => {
  const startNode = nodes.find(n => n.id === beam.startNodeId);
  const endNode = nodes.find(n => n.id === beam.endNodeId);
  
  if (!startNode || !endNode) {
    return null;
  }

  const centerPoint = {
    x: (startNode.x + endNode.x) / 2,
    y: (startNode.y + endNode.y) / 2
  };

  const length = calculateBeamLength(beam, nodes);
  const angle = Math.atan2(endNode.y - startNode.y, endNode.x - startNode.x);

  return {
    centerPoint,
    length,
    angle
  };
};

/**
 * Check if a point is close to a beam center (within tolerance)
 */
export const isPointOnBeamCenter = (
  point: Point2D, 
  beam: Beam, 
  nodes: Node[], 
  tolerance: number = 8
): boolean => {
  const centerPoint = calculateBeamCenter(beam, nodes);
  if (!centerPoint) return false;

  const distance = Math.sqrt(
    Math.pow(point.x - centerPoint.x, 2) + 
    Math.pow(point.y - centerPoint.y, 2)
  );

  return distance <= tolerance;
};

/**
 * Find beam at a given position using point-to-line distance calculation
 */
export const findBeamAtPosition = (
  pos: Point2D, 
  beams: Beam[], 
  nodes: Node[], 
  tolerance: number = 5
): Beam | null => {
  return beams.find(beam => {
    const startNode = nodes.find(n => n.id === beam.startNodeId);
    const endNode = nodes.find(n => n.id === beam.endNodeId);
    
    if (!startNode || !endNode) return false;
    
    return isPointOnLineSegment(pos, startNode, endNode, tolerance);
  }) || null;
};

/**
 * Check if a point is on a line segment within tolerance
 */
export const isPointOnLineSegment = (
  point: Point2D, 
  lineStart: Node, 
  lineEnd: Node, 
  tolerance: number
): boolean => {
  const distance = distanceFromPointToLineSegment(point, lineStart, lineEnd);
  return distance <= tolerance;
};

/**
 * Calculate distance from point to line segment
 */
export const distanceFromPointToLineSegment = (
  point: Point2D, 
  lineStart: Node, 
  lineEnd: Node
): number => {
  const A = point.x - lineStart.x;
  const B = point.y - lineStart.y;
  const C = lineEnd.x - lineStart.x;
  const D = lineEnd.y - lineStart.y;
  
  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  const param = lenSq !== 0 ? dot / lenSq : -1;
  
  let xx, yy;
  
  if (param < 0) {
    xx = lineStart.x;
    yy = lineStart.y;
  } else if (param > 1) {
    xx = lineEnd.x;
    yy = lineEnd.y;
  } else {
    xx = lineStart.x + param * C;
    yy = lineStart.y + param * D;
  }
  
  const dx = point.x - xx;
  const dy = point.y - yy;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Create a new node at beam center and split beam into two segments
 */
export const createNodeAtBeamCenter = (
  beam: Beam, 
  nodes: Node[], 
  getNextNodeId: () => string, 
  getNextBeamId: () => string,
  _nodeNumber: number,
  _beamNumber: number
): BeamSplitResult | null => {
  const centerPoint = calculateBeamCenter(beam, nodes);
  if (!centerPoint) return null;

  // Create new node at center
  const newNode: Node = {
    id: getNextNodeId(),
    x: centerPoint.x,
    y: centerPoint.y,
    z: nodes.find(n => n.id === beam.startNodeId)?.z || 0, // Use start node Z coordinate
    isSelected: false,
    color: '#4dabf7' // Default node color
  };

  // Create two new beams with simple serial numbering
  const beam1: Beam = {
    ...beam,
    id: getNextBeamId(),
    endNodeId: newNode.id
  };

  const beam2: Beam = {
    ...beam,
    id: getNextBeamId(),
    startNodeId: newNode.id
  };

  return {
    newNode,
    beam1,
    beam2,
    originalBeamId: beam.id
  };
};

/**
 * Generate simple serial beam label
 */
export const generateBeamLabel = (beamNumber: number): string => {
  // Convert to 1-based index for display
  return `${beamNumber}`;
};

/**
 * Calculate label position for beam - positioned above beam center for better visibility
 */
export const calculateBeamLabelPosition = (
  beam: Beam, 
  nodes: Node[], 
  offsetX: number = 0, 
  offsetY: number = 0.8 // Position above beam
): { x: number; y: number; z: number } | null => {
  const startNode = nodes.find(n => n.id === beam.startNodeId);
  const endNode = nodes.find(n => n.id === beam.endNodeId);
  
  if (!startNode || !endNode) return null;

  // Calculate center point in 3D
  const centerPoint = {
    x: (startNode.x + endNode.x) / 2,
    y: (startNode.y + endNode.y) / 2,
    z: (startNode.z + endNode.z) / 2
  };

  // Position label above the beam center
  return {
    x: centerPoint.x + offsetX,
    y: centerPoint.y + offsetY, // Always above the beam
    z: centerPoint.z
  };
};

/**
 * Check if click is specifically on beam center point
 */
export const isClickOnBeamCenter = (
  clickPos: Point2D, 
  beam: Beam, 
  nodes: Node[], 
  tolerance: number = 8
): boolean => {
  return isPointOnBeamCenter(clickPos, beam, nodes, tolerance);
};

// Additional types for enhanced beam operations
export interface Point3D {
  x: number;
  y: number;
  z: number;
}

export interface BeamPositionResult {
  position: Point3D;
  parameter: number; // 0-1 along beam length
  isValid: boolean;
}

/**
 * Calculate the exact position along a beam from a 3D click point
 * Projects the click point onto the beam line and returns the precise position
 */
export const calculatePositionOnBeam = (
  clickPoint: Point3D,
  beam: Beam,
  nodes: Node[]
): BeamPositionResult => {
  const startNode = nodes.find(n => n.id === beam.startNodeId);
  const endNode = nodes.find(n => n.id === beam.endNodeId);
  
  if (!startNode || !endNode) {
    return { position: clickPoint, parameter: 0, isValid: false };
  }

  // Create 3D vectors for beam and click point
  const beamStart = { x: startNode.x, y: startNode.y, z: startNode.z };
  const beamEnd = { x: endNode.x, y: endNode.y, z: endNode.z };
  
  // Calculate beam direction vector
  const beamVector = {
    x: beamEnd.x - beamStart.x,
    y: beamEnd.y - beamStart.y,
    z: beamEnd.z - beamStart.z
  };
  
  // Calculate vector from beam start to click point
  const clickVector = {
    x: clickPoint.x - beamStart.x,
    y: clickPoint.y - beamStart.y,
    z: clickPoint.z - beamStart.z
  };
  
  // Calculate beam length squared
  const beamLengthSq = beamVector.x * beamVector.x + 
                       beamVector.y * beamVector.y + 
                       beamVector.z * beamVector.z;
  
  if (beamLengthSq === 0) {
    return { position: beamStart, parameter: 0, isValid: false };
  }
  
  // Calculate projection parameter (0-1 along beam)
  const dotProduct = clickVector.x * beamVector.x + 
                     clickVector.y * beamVector.y + 
                     clickVector.z * beamVector.z;
  
  let parameter = dotProduct / beamLengthSq;
  
  // Clamp parameter to beam bounds (0-1)
  parameter = Math.max(0, Math.min(1, parameter));
  
  // Calculate exact position on beam
  const position: Point3D = {
    x: beamStart.x + parameter * beamVector.x,
    y: beamStart.y + parameter * beamVector.y,
    z: beamStart.z + parameter * beamVector.z
  };
  
  return {
    position,
    parameter,
    isValid: true
  };
};

/**
 * Create a new node at any position along a beam and split beam into two segments
 */
export const createNodeAtBeamPosition = (
  beam: Beam,
  nodes: Node[],
  position: Point3D,
  parameter: number,
  getNextNodeId: () => string,
  getNextBeamId: () => string,
  _nodeNumber: number,
  _beamNumber: number
): BeamSplitResult | null => {
  const startNode = nodes.find(n => n.id === beam.startNodeId);
  const endNode = nodes.find(n => n.id === beam.endNodeId);
  
  if (!startNode || !endNode || parameter < 0 || parameter > 1) {
    return null;
  }

  // Create new node at calculated position
  const newNode: Node = {
    id: getNextNodeId(),
    x: position.x,
    y: position.y,
    z: position.z,
    isSelected: false,
    color: '#4dabf7' // Default node color
  };

  // Create two new beams preserving original properties
  const beam1: Beam = {
    ...beam,
    id: getNextBeamId(),
    endNodeId: newNode.id
  };

  const beam2: Beam = {
    ...beam,
    id: getNextBeamId(),
    startNodeId: newNode.id
  };

  return {
    newNode,
    beam1,
    beam2,
    originalBeamId: beam.id
  };
};

/**
 * Find the closest beam to a 3D click point and calculate position on it
 */
export const findBeamAtPosition3D = (
  clickPoint: Point3D,
  beams: Beam[],
  nodes: Node[],
  tolerance: number = 1.0
): { beam: Beam; positionResult: BeamPositionResult } | null => {
  let closestBeam: Beam | null = null;
  let closestPositionResult: BeamPositionResult | null = null;
  let minDistance = tolerance;

  for (const beam of beams) {
    const positionResult = calculatePositionOnBeam(clickPoint, beam, nodes);
    
    if (!positionResult.isValid) continue;

    // Calculate distance from click point to projected position on beam
    const dx = clickPoint.x - positionResult.position.x;
    const dy = clickPoint.y - positionResult.position.y;
    const dz = clickPoint.z - positionResult.position.z;
    const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

    if (distance < minDistance) {
      minDistance = distance;
      closestBeam = beam;
      closestPositionResult = positionResult;
    }
  }

  if (closestBeam && closestPositionResult) {
    return {
      beam: closestBeam,
      positionResult: closestPositionResult
    };
  }

  return null;
}; 